import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

// kleiner Helper: Eurowert anzeigen oder Strich. Wert kommt vom Backend
// als DECIMAL-String (z. B. "42.50") oder null.
const fmtSatz = (v) => {
  if (v == null || v === '') return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return '–';
  return n.toFixed(2) + ' €/h';
};

export default function Team() {
  const { user: me } = useAuth() || {};
  const [users, setUsers] = useState([]);
  const [rollen, setRollen] = useState([]);       // Funktionsrollen (Monteur, Bauleiter, ...)
  const [editing, setEditing] = useState(null);   // null = closed, {} = neu, {id,...} = bearbeiten
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('alle');   // 'alle' | 'aktiv' | 'inaktiv'

  const load = () => api.get('/users')
    .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
    .catch(() => setUsers([]));

  const loadRollen = () => api.get('/rollen')
    .then(r => setRollen(Array.isArray(r.data) ? r.data : []))
    .catch(() => setRollen([]));

  useEffect(() => { load(); loadRollen(); }, []);

  // Nur aktive Rollen im Dropdown, sortiert nach reihenfolge (Backend liefert
  // schon so, hier defensiv filtern - inaktive Rollen koennen aber als
  // bestehende Zuordnung auf einem User weiter erscheinen).
  const rollenAktiv = rollen.filter(r => r.aktiv);

  const save = async (data) => {
    try {
      if (editing?.id) {
        // Edit: leeres Passwort heißt "unverändert" — Schlüssel weglassen, Backend ignoriert dann.
        const payload = { ...data };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post('/users', data);
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Speichern fehlgeschlagen');
    }
  };

  const toggleAktiv = async (u) => {
    const aktion = u.aktiv ? 'deaktivieren' : 'reaktivieren';
    if (!window.confirm(
      `Mitarbeiter "${u.name || u.username}" ${aktion}?\n` +
      `Bestehende Bautagebücher und Zeiterfassungen bleiben erhalten.`
    )) return;
    try {
      await api.put(`/users/${u.id}`, { aktiv: !u.aktiv });
      load();
    } catch (e) {
      alert(e.response?.data?.error || `Aktion fehlgeschlagen`);
    }
  };

  // Sortierung: aktive zuerst, je Gruppe alphabetisch nach Anzeigename
  const sorted = [...users].sort((a, b) => {
    if (a.aktiv !== b.aktiv) return a.aktiv ? -1 : 1;
    const an = (a.name || a.username || '').toLowerCase();
    const bn = (b.name || b.username || '').toLowerCase();
    return an.localeCompare(bn);
  });
  const visible = sorted.filter(u =>
    filter === 'alle' ? true : (filter === 'aktiv' ? u.aktiv : !u.aktiv)
  );

  const aktivCount = users.filter(u => u.aktiv).length;
  const inaktivCount = users.length - aktivCount;

  const segBtn = (v, label) => (
    <button
      key={v}
      type="button"
      onClick={() => setFilter(v)}
      style={{
        background: filter === v ? 'var(--bg-card)' : 'transparent',
        border: 'none',
        padding: '6px 12px',
        borderRadius: 'var(--radius)',
        fontSize: 12,
        fontWeight: 600,
        color: filter === v ? 'var(--copper)' : 'var(--text-2)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        boxShadow: filter === v ? 'var(--shadow)' : 'none',
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Team</div>
          <div className="page-sub">{users.length} Mitarbeiter · {aktivCount} aktiv · {inaktivCount} inaktiv</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditing({}); setShowForm(true); }}>+ Neuer Mitarbeiter</button>
      </div>

      <div className="card">
        <div className="card-head">
          <div style={{ display: 'flex', gap: 4, background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: 4 }}>
            {segBtn('alle', 'Alle')}
            {segBtn('aktiv', 'Aktiv')}
            {segBtn('inaktiv', 'Inaktiv')}
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="empty"><div className="empty-icon">👥</div><h3>Keine Mitarbeiter</h3><p>Filter auf „Alle" stellen oder neuen Mitarbeiter anlegen.</p></div>
        ) : (
          <div className="table-wrap table-mobile-stack">
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Benutzername</th><th>Email</th><th>Funktion</th><th>Std.satz</th><th>Rolle</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map(u => {
                  const isSelf = me?.id === u.id;
                  return (
                    <tr key={u.id} style={{ opacity: u.aktiv ? 1 : 0.6 }}>
                      <td data-label="" className="td-title" style={{ fontWeight: 500 }}>{u.name || u.username}</td>
                      <td data-label="Benutzername" className="text-mono">{u.username}</td>
                      <td data-label="Email">{u.email || '–'}</td>
                      <td data-label="Funktion">{u.rolle_bezeichnung || '–'}</td>
                      <td data-label="Std.satz" className="text-mono">{fmtSatz(u.stundensatz_intern)}</td>
                      <td data-label="Rolle">{u.role === 'admin' ? <span className="badge badge-copper">Admin</span> : <span className="badge badge-blue">Monteur</span>}</td>
                      <td data-label="Status">{u.aktiv ? <span className="badge badge-green">Aktiv</span> : <span className="badge badge-grey">Inaktiv</span>}</td>
                      <td data-label="" className="td-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(u); setShowForm(true); }} title="Bearbeiten">✏️</button>
                        {u.aktiv ? (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => toggleAktiv(u)}
                            disabled={isSelf}
                            title={isSelf ? 'Du kannst dich nicht selbst deaktivieren' : 'Deaktivieren'}
                          >🗑</button>
                        ) : (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => toggleAktiv(u)}
                            title="Reaktivieren"
                          >↩️</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <UserForm
          user={editing}
          rollen={rollenAktiv}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function UserForm({ user, rollen, onClose, onSave }) {
  const isEdit = !!user?.id;
  const [data, setData] = useState({
    username: user?.username || '',
    password: '',
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'monteur',
    // A.4: Funktions-Rolle (rolle_id) + interner Stundensatz. Beides optional.
    // Leer-String wird beim Speichern an Backend gesendet und dort als NULL behandelt.
    rolle_id: user?.rolle_id != null ? String(user.rolle_id) : '',
    stundensatz_intern: user?.stundensatz_intern != null ? String(user.stundensatz_intern) : '',
  });

  // Wenn die aktuell zugewiesene rolle_id im Aktiv-Set nicht (mehr) vorkommt,
  // hängen wir sie defensiv an die Auswahl an (Bestandsschutz für deaktivierte
  // Rollen-Zuordnungen). Die Liste rollen kommt vom Parent bereits gefiltert
  // auf aktiv=true.
  const currentRolleId = user?.rolle_id;
  const currentRolleInList = rollen?.some(r => r.id === currentRolleId);
  const dropdownRollen = (!currentRolleInList && user?.rolle_id != null && user?.rolle_bezeichnung)
    ? [...(rollen || []), {
        id: user.rolle_id,
        bezeichnung: `${user.rolle_bezeichnung} (deaktiviert)`,
        aktiv: false,
      }]
    : (rollen || []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={onClose}>
      <div className="card" style={{ maxWidth: 500, width: '100%', margin: 0, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="card-title" style={{ marginBottom: 14 }}>{isEdit ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}</div>

        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">Name</label>
          <input className="form-input" value={data.name} onChange={e => setData({ ...data, name: e.target.value })} />
        </div>

        <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Benutzername</label>
            <input
              className="form-input"
              value={data.username}
              onChange={e => setData({ ...data, username: e.target.value })}
              required
              autoComplete="off"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Passwort{isEdit ? ' (optional)' : ''}</label>
            <input
              className="form-input"
              type="password"
              value={data.password}
              onChange={e => setData({ ...data, password: e.target.value })}
              required={!isEdit}
              placeholder={isEdit ? 'leer = unverändert' : ''}
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={data.email} onChange={e => setData({ ...data, email: e.target.value })} />
        </div>

        {/* A.4: Funktions-Rolle + interner Stundensatz. Beide optional, leer = nicht gesetzt. */}
        <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Funktion</label>
            <select
              className="form-select"
              value={data.rolle_id}
              onChange={e => setData({ ...data, rolle_id: e.target.value })}
            >
              <option value="">— keine —</option>
              {dropdownRollen.map(r => (
                <option key={r.id} value={r.id}>{r.bezeichnung}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Stundensatz intern (€/h)</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={data.stundensatz_intern}
              placeholder="optional"
              onChange={e => setData({ ...data, stundensatz_intern: e.target.value })}
            />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Rolle</label>
          <select className="form-select" value={data.role} onChange={e => setData({ ...data, role: e.target.value })}>
            <option value="monteur">Monteur</option>
            <option value="admin">Administrator</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => onSave(data)}>{isEdit ? 'Speichern' : 'Anlegen'}</button>
        </div>
      </div>
    </div>
  );
}
